/**
 * Targeting Rule Builder
 * 
 * Main component for building and editing targeting rules
 */

import { useState, useCallback, useEffect } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Button,
  Text,
  Banner,
  Spinner,
} from "@shopify/polaris";
import type { TargetingRuleV1, Condition, LogicalCondition, ValidationError } from "./types";
import { GroupEditor } from "./GroupEditor";
import { PreviewPanel } from "./PreviewPanel";

interface RuleBuilderProps {
  rule: TargetingRuleV1 | null;
  onChange: (rule: TargetingRuleV1 | null) => void;
  onValidate?: (rule: TargetingRuleV1) => Promise<{ valid: boolean; errors: ValidationError[] }>;
  showPreview?: boolean;
}

export function RuleBuilder({
  rule,
  onChange,
  onValidate,
  showPreview = true,
}: RuleBuilderProps) {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);

  /**
   * Validate rule on change (debounced)
   */
  useEffect(() => {
    if (!rule || !onValidate) {
      setErrors([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsValidating(true);
      try {
        const result = await onValidate(rule);
        setErrors(result.valid ? [] : result.errors);
      } catch (error) {
        console.error("Validation failed:", error);
        setErrors([
          {
            path: "rule",
            message: "Validation failed. Please check your rule.",
            code: "VALIDATION_ERROR",
          },
        ]);
      } finally {
        setIsValidating(false);
      }
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [rule, onValidate]);

  /**
   * Initialize empty rule
   */
  const handleEnableTargeting = useCallback(() => {
    const newRule: TargetingRuleV1 = {
      version: "1",
      rule: {
        type: "and",
        conditions: [],
      },
    };
    onChange(newRule);
  }, [onChange]);

  /**
   * Disable targeting
   */
  const handleDisableTargeting = useCallback(() => {
    onChange(null);
    setErrors([]);
  }, [onChange]);

  /**
   * Update root rule
   */
  const handleRuleChange = useCallback(
    (newRootCondition: Condition) => {
      if (!rule) return;

      const updatedRule: TargetingRuleV1 = {
        ...rule,
        rule: newRootCondition,
      };

      onChange(updatedRule);
    },
    [rule, onChange]
  );

  // Check if rule is a logical group
  const isLogicalGroup = (condition: Condition | undefined): boolean => {
    if (!condition) return false;
    return "conditions" in condition && Array.isArray(condition.conditions);
  };

  if (!rule) {
    return (
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Targeting Rules
          </Text>

          <Text as="p">
            Targeting rules allow you to show this campaign only to specific customers based on
            their profile, cart contents, location, and more.
          </Text>

          <Banner tone="info">
            <Text as="p">
              Targeting is optional. If not enabled, this campaign will be shown to all eligible
              customers.
            </Text>
          </Banner>

          <InlineStack>
            <Button onClick={handleEnableTargeting}>Enable Targeting</Button>
          </InlineStack>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              Targeting Rules
            </Text>

            <InlineStack gap="200">
              {isValidating && <Spinner size="small" />}

              {showPreview && (
                <Button
                  onClick={() => setShowPreviewPanel(!showPreviewPanel)}
                  variant="plain"
                >
                  {showPreviewPanel ? "Hide" : "Show"} Preview
                </Button>
              )}

              <Button
                onClick={handleDisableTargeting}
                variant="plain"
                tone="critical"
              >
                Disable Targeting
              </Button>
            </InlineStack>
          </InlineStack>

          {errors.length > 0 && (
            <Banner tone="critical">
              <BlockStack gap="100">
                <Text as="p" variant="headingSm">
                  Validation Errors ({errors.length})
                </Text>
                {errors.slice(0, 5).map((error, idx) => (
                  <Text key={idx} as="p">
                    • {error.path}: {error.message}
                  </Text>
                ))}
                {errors.length > 5 && (
                  <Text as="p" tone="subdued">
                    ... and {errors.length - 5} more
                  </Text>
                )}
              </BlockStack>
            </Banner>
          )}

          {isLogicalGroup(rule.rule) ? (
            <GroupEditor
              group={rule.rule as LogicalCondition}
              onChange={handleRuleChange}
              errors={errors}
              path="rule"
              depth={0}
            />
          ) : (
            <Banner tone="warning">
              <Text as="p">
                Root condition must be a logical group (AND/OR/NOT). Please recreate the rule.
              </Text>
            </Banner>
          )}
        </BlockStack>
      </Card>

      {showPreviewPanel && <PreviewPanel rule={rule} />}
    </BlockStack>
  );
}
